// @/services/app/store/modules/announcements.js

/**
 * AnnouncementsHandler
 *
 * Per-user, multi-instance in-app announcement system. Replaces the legacy
 * single-purpose RelaunchDialogHandler. Pairs with the backend
 * `/api/announcements/*` endpoints (api#49+).
 *
 *   - GET  /api/announcements/active        → live list with view-state pre-joined
 *   - POST /api/announcements/:key/track    → record action + cross-tab broadcast
 *
 * Authorization is the user's Keycloak token (NOT the static GATSBY_STRAPI_TOKEN)
 * so the backend's decode-keycloak-user policy can extract `sub` for per-user
 * tracking. Per the Keycloak-cruise-ship feedback memory: the token comes from
 * authService, never imported from keycloak directly.
 */

import { authService } from '@/services/auth';

const ACTIVE_CACHE_MS = 5 * 60 * 1000; // 5 min, matches the canonical plan
const STATE_KEY_PREFIX = 'announcement_state_';
const CHECK_AT_KEY = 'announcement_check_at';

export class AnnouncementsHandler {
  constructor(store) {
    this.store = store;
    this.fetchAbortController = null;
    this.trackAbortController = null;
    this.cached = null; // { at: number, items: Array }
  }

  _authHeader() {
    const token = authService.getAuthState()?.token;
    return token ? { Authorization: `Bearer ${token}` } : null;
  }

  _logSkip(reason, extra) {
    console.log(`[AnnouncementHost] skip reason=${reason}`, extra ?? '');
  }

  /**
   * Fetch active announcements for the calling user. Throttled to 5 min by
   * an in-handler cache + a localStorage timestamp so cross-tab calls also
   * cooperate.
   *
   * @param {string} locale
   * @param {{force?: boolean}} [opts]
   * @returns {Promise<Array>}
   */
  async fetchActive(locale = 'en', { force = false } = {}) {
    const headers = this._authHeader();
    if (!headers) {
      this._logSkip('no_auth_token');
      return [];
    }

    if (!force && this.cached && Date.now() - this.cached.at < ACTIVE_CACHE_MS) {
      return this.cached.items;
    }

    if (!force && typeof localStorage !== 'undefined') {
      const lastCheck = parseInt(localStorage.getItem(CHECK_AT_KEY) || '0', 10);
      if (lastCheck && Date.now() - lastCheck < ACTIVE_CACHE_MS && this.cached) {
        return this.cached.items;
      }
    }

    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
    }
    this.fetchAbortController = new AbortController();

    try {
      const url = `${process.env.GATSBY_STRAPI_URL}/api/announcements/active?locale=${encodeURIComponent(locale)}`;
      const response = await fetch(url, {
        headers: { ...headers, Accept: 'application/json' },
        signal: this.fetchAbortController.signal,
        cache: 'no-cache',
      });

      if (!response.ok) {
        this._logSkip('fetch_non_ok', { status: response.status });
        return [];
      }

      const json = await response.json();
      const items = Array.isArray(json?.data) ? json.data : [];

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(CHECK_AT_KEY, String(Date.now()));
      }
      this.cached = { at: Date.now(), items };

      console.log(`[AnnouncementHost] active fetched count=${items.length} locale=${locale}`);
      return items;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[AnnouncementHost] fetch error', error);
      }
      // Fail-open: return whatever we cached most recently, even if stale.
      return this.cached?.items || [];
    } finally {
      this.fetchAbortController = null;
    }
  }

  /**
   * Record an action against an announcement. Updates localStorage + broadcasts
   * a cross-tab storage event so siblings dismiss without flashing.
   *
   * @param {string} key
   * @param {'viewed'|'dismissed'|'cta_clicked'|'deferred'} action
   * @returns {Promise<boolean>}
   */
  async track(key, action = 'viewed') {
    if (!key) return false;

    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(
          `${STATE_KEY_PREFIX}${key}`,
          JSON.stringify({ action, at: Date.now() })
        );
      } catch {
        // Storage quota — ignore.
      }
    }

    // Drop the cached active list so the next fetch reflects the new state.
    this.cached = null;

    const headers = this._authHeader();
    if (!headers) {
      console.log(`[AnnouncementHost] track skipped — no auth token (key=${key})`);
      return true;
    }

    if (this.trackAbortController) this.trackAbortController.abort();
    this.trackAbortController = new AbortController();

    try {
      const response = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/announcements/${encodeURIComponent(key)}/track`,
        {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
          signal: this.trackAbortController.signal,
        }
      );
      if (!response.ok) {
        console.warn(`[AnnouncementHost] track non-ok status=${response.status} key=${key}`);
      }
      return true;
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[AnnouncementHost] track error', error);
      }
      return true;
    } finally {
      this.trackAbortController = null;
    }
  }

  /**
   * Parse a cross-tab storage event for an announcement state change.
   * Returns the announcement key if this event is one of ours, else null.
   */
  parseStorageEvent(event) {
    if (!event?.key || !event.key.startsWith(STATE_KEY_PREFIX)) return null;
    return event.key.substring(STATE_KEY_PREFIX.length);
  }

  cleanup() {
    if (this.fetchAbortController) this.fetchAbortController.abort();
    if (this.trackAbortController) this.trackAbortController.abort();
    this.fetchAbortController = null;
    this.trackAbortController = null;
  }
}
